<template>
  <div class="text-gray-600">
    <div class="hero-header py-10 px-4 md:flex md:flex-row-reverse max-w-946">
      <div class="rounded md:flex-1 md:pl-9">
        <OImage v-bind="heroImage" img-class="rounded" />
      </div>
      <div class="md:flex-1">
        <h1 class="o-heading-1 mt-6 mb-1 md:mt-0" v-text="title" />
        <p class="o-body orange-body" v-html="description" />
        <div class="mt-6">
          <NewButton tag="nuxt-link" :to="{ path: $i18n.localePath({ name: 'signup' }) }" icon-right="new-chevron-right">
            {{ $t('TryItNow.IWantThis') }}
          </NewButton>
        </div>
      </div>
    </div>
    <DiscoverLoyaltyProducts
      :is-women-country="isWomenCountry"
    />
    <WhyOta
      :country="country"
      class="mt-10"
      :button-text="isTwoForOneCampaign ? $t('TwoForOneCampaign.TryForFree') : $t('TryItNow.IWantThis')"
      :is-women="isWomenCountry"
    />
    <div class="container no-padding-container">
      <StarRating
        v-if="rating"
        class="mt-16 pb-10 pt-20 md:pb-20"
        :title="'TryItNow.ReviewsTitle'"
        :rating="rating"
        :reviews="ratingTotal"
        @click="onRatingClick"
      />
    </div>
    <div class="bg-neutral-50">
      <div class="container no-padding-container">
        <Reviews title="" />
        <p class="o-body text-center py-5" v-text="$t('HomePage.SelectedFavoriteReviews')" />
      </div>
    </div>
    <div class="my-20 faq-container px-4">
      <Faq :items="faqItems" />
    </div>
  </div>
</template>

<script>
import Faq from '@/components/Faq'
import { getImagePaths, capitalize, addLocalCurrency } from '@/utils/general'
import { MenPrices } from '@/enums/products'
import { mapState, mapActions, mapGetters } from 'vuex'
import { Currencies, WomenCountries } from '@/enums/general'
import { GET_GOOGLE_RATING, GET_FLAGS } from '@/utils/store/actionTypes'
import Reviews from '@/components/Reviews'
import StarRating from '@/components/StarRating'
import WhyOta from '@/components/WhyOta'
import OImage from '@/components/OImage'
import NewButton from '@/components/NewButton'
import DiscoverLoyaltyProducts from '@/components/DiscoverLoyaltyProducts'
import { DeliveryTimes } from '@/enums/shipments'
import { TrialDuration } from '@/enums/member'
import { WebsiteCategoryCookie } from '@/enums/app'
import { IS_FLAGS_FETCHED, IS_TWO_FOR_ONE_CAMPAIGN } from '@/utils/store/getterTypes'

export default {
  name: 'ReferredByFriend',
  layout: 'homepage',
  components: { Faq, Reviews, StarRating, WhyOta, OImage, NewButton, DiscoverLoyaltyProducts },
  async fetch () {
    await Promise.all([
      this.getAndSetFlags(),
      this.getAndSetGoogleRating()
    ])
  },
  data () {
    return {
      rating: null,
      ratingTotal: null
    }
  },
  computed: {
    isWomenCountry () {
      return WomenCountries.includes(this.country)
    },
    faqItems () {
      const path = this.isWomenCountry ? 'SignUp.StepThree.MixedFaqItems' : 'SignUp.StepThree.FaqItems'
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, i) => ({
        title: this.$t(`${path}[${i}].title`, { ...this.variables }),
        description: this.$t(`${path}[${i}].description`, { ...this.variables })
      }))
    },
    heroImage () {
      return getImagePaths({
        s3: true,
        path: this.isWomenCountry ? '/images/frontend/loyalty/header-membership-mix' : '/images/frontend/loyalty/header-membership-men'
      })
    },
    variables () {
      return {
        trialDuration: TrialDuration[this.country],
        earliest: DeliveryTimes[this.country].earliest,
        latest: DeliveryTimes[this.country].latest,
        loyaltyAmount: addLocalCurrency(10, `${this.locale}-${this.country.toUpperCase()}`, Currencies.nl),
        maxLoyaltyAmount: addLocalCurrency(20, `${this.locale}-${this.country.toUpperCase()}`, Currencies.nl),
        price: addLocalCurrency(MenPrices[this.country], `${this.locale}-${this.country.toUpperCase()}`, Currencies[this.country]),
        country: this.$t(`Countries.${this.country}`)
      }
    },
    title () {
      const name = this.$route.query.name

      if (name) {
        if (this.isWomenCountry) {
          return this.$t('LandingsPage.ReferredByPage.WomenTitleWithName', { name: capitalize(name), ...this.variables })
        }

        return this.$t('LandingsPage.ReferredByPage.TitleWithName', { name: capitalize(name), ...this.variables })
      }

      if (this.isWomenCountry) {
        return this.$t('LandingsPage.ReferredByPage.WomenTitleWithoutName', { ...this.variables })
      }

      return this.$t('LandingsPage.ReferredByPage.TitleWithoutName', { ...this.variables })
    },
    description () {
      if (this.isWomenCountry) {
        return this.$t('LandingsPage.ReferredByPage.WomenDescription', {
          ...this.variables
        })
      }

      return this.$t('LandingsPage.ReferredByPage.Description', {
        ...this.variables
      })
    },
    ...mapState('app', {
      country: state => state.country,
      locale: state => state.locale
    }),
    ...mapGetters('flags', {
      isFlagsFetched: IS_FLAGS_FETCHED,
      isTwoForOneCampaign: IS_TWO_FOR_ONE_CAMPAIGN
    })
  },
  mounted () {
    this.$cookies.remove(WebsiteCategoryCookie)
  },
  methods: {
    getImagePaths (path) {
      return getImagePaths({
        path: `/images/frontend/loyalty/referral/${path}`,
        s3: true
      })
    },
    onRatingClick () {
      window.open(this.$t('HomePage.GoogleReviewPage'), '_blank', 'noopener')
    },
    async getAndSetGoogleRating () {
      try {
        const { rating, ratingTotal } = await this.getGoogleRating()

        this.rating = rating
        this.ratingTotal = ratingTotal
      } catch (error) {
        this.$ota.general.logError(error)
      }
    },
    async getAndSetFlags () {
      if (this.isFlagsFetched) {
        return
      }

      try {
        await this.getFlags()
      } catch (error) {
        this.$ota.general.logError(error)
      }
    },
    ...mapActions('homepage', {
      getGoogleRating: GET_GOOGLE_RATING
    }),
    ...mapActions('flags', {
      getFlags: GET_FLAGS
    })
  }
}
</script>

<style lang="postcss" scoped>
.faq-container {
  @apply mx-auto;
  max-width: 54rem;

  >>> .faq-body svg {
    @apply fill-neutral-800;
  }
}

.max-w-946 {
  @apply mx-auto;
  max-width: 94.6rem;
}

.orange-body >>> b {
  @apply text-orange-300;
}

h1 {
  line-height: normal;
}

.max-container-width {
  max-width: 91rem;
}

.oimage >>> .max-w-full {
  @apply max-w-full !important
}

@screen md {
  .oimage >>> .referral-header {
    min-width: 40rem;
  }

  .hero-header .oimage >>> img {
    max-width: unset;
  }
}
</style>
